import React, { useState } from 'react';
import WakeWordDetector from '../../WakeWordDetector';

interface VoiceControlsProps {
  accessKey: string;
  wakeWordEnabled: boolean;
  speechInitialized: boolean;
  onWakeWordDetected: () => void;
  onVoiceCommand: (command: string) => void;
  onWakeWordToggle: () => void;
  onInitializeSpeech: () => void;
}

const VOICE_COMMAND_CATEGORIES = [
  {
    title: 'Navigation',
    commands: [
      '"Go to appointments" / "Show me my schedule"',
      '"Pull up clients" / "Take me to patients"',
      '"Open messages" / "Show me inbox"',
      '"Go to billing" / "Pull up invoices"',
      '"Open H R" / "Show me documents"',
      '"Go to settings" / "Open preferences"',
      '"Show me meetings" / "Go to team chat"',
      '"Open tasks" / "Show my to-do list"',
    ],
  },
  {
    title: 'Appointments',
    commands: [
      '"What are my appointments?"',
      '"How many appointments do I have?"',
      '"Who\'s next?" / "Next appointment"',
    ],
  },
  {
    title: 'Utility',
    commands: [
      '"What time is it?"',
      '"Help" / "What can you do?"',
    ],
  },
  {
    title: 'Control',
    commands: [
      '"Stop" / "Be quiet" — silence Clinic',
      '"Stop listening" / "Turn off" — disable voice',
      '"Never mind" — cancel current response',
    ],
  },
];

const VoiceControls: React.FC<VoiceControlsProps> = ({
  accessKey,
  wakeWordEnabled,
  speechInitialized,
  onWakeWordDetected,
  onVoiceCommand,
  onWakeWordToggle,
  onInitializeSpeech
}) => {
  const [showCommands, setShowCommands] = useState(false);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', position: 'relative' }}>
      {/* Wake Word Detection */}
      <WakeWordDetector
        accessKey={accessKey}
        onWakeWordDetected={onWakeWordDetected}
        onVoiceCommand={onVoiceCommand}
        isEnabled={wakeWordEnabled}
        onStatusChange={() => {}}
      />

      {/* Wake Word Toggle */}
      <button
        onClick={() => {
          onInitializeSpeech();
          onWakeWordToggle();
        }}
        style={{
          background: wakeWordEnabled ? '#D4A017' : '#6b7280',
          color: 'white',
          border: 'none',
          padding: '0.5rem 1rem',
          borderRadius: '0.5rem',
          fontSize: '0.75rem',
          cursor: 'pointer'
        }}
      >
        {wakeWordEnabled ? 'Voice ON' : 'Voice OFF'}
      </button>

      {/* Command Help Bubble */}
      <button
        onClick={() => setShowCommands(!showCommands)}
        title="Voice command reference"
        style={{
          width: '28px',
          height: '28px',
          borderRadius: '50%',
          border: '1px solid #6CA6C1',
          backgroundColor: showCommands ? '#D4A017' : '#28527A',
          color: showCommands ? '#1e293b' : '#F7E9A0',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '700',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        ?
      </button>

      {/* Command Reference Popup */}
      {showCommands && (
        <div style={{
          position: 'absolute',
          top: '100%',
          right: 0,
          marginTop: '8px',
          width: '360px',
          maxHeight: '70vh',
          overflowY: 'auto',
          backgroundColor: '#1e293b',
          border: '1px solid #6CA6C1',
          borderRadius: '10px',
          padding: '16px',
          zIndex: 2000,
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h4 style={{ margin: 0, color: '#F7E9A0', fontSize: '14px' }}>
              "Hey Clinic" Voice Commands
            </h4>
            <button
              onClick={() => setShowCommands(false)}
              style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '16px' }}
            >
              x
            </button>
          </div>
          <p style={{ color: '#9ca3af', fontSize: '11px', margin: '0 0 12px 0' }}>
            Say "Hey Clinic" followed by any command below:
          </p>
          {VOICE_COMMAND_CATEGORIES.map(cat => (
            <div key={cat.title} style={{ marginBottom: '12px' }}>
              <div style={{ color: '#D4A017', fontSize: '12px', fontWeight: '600', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {cat.title}
              </div>
              {cat.commands.map((cmd, i) => (
                <div key={i} style={{ color: '#d1d5db', fontSize: '12px', padding: '2px 0', lineHeight: '1.5' }}>
                  {cmd}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VoiceControls;